import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Modal, Spinner, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import Select from 'react-select/creatable';

import {
  backgroundGrey5,
  blackberry,
  coolGrey,
  grape,
  greyStroke,
  salmon,
  softAmber,
  softAmberLight,
  softGreen,
  softGreenLight,
  squidInk,
  white,
} from 'lib/css/colors';
import { GLOBAL_MANUFACTURERS_MERGE } from 'lib/networking/endpoints';
import { ManufacturerVerificationStatus } from 'lib/enums/ManufacturerVerificationStatus';
import { selectStyles } from 'lib/css/selectStyles';
import SwalDialog from 'lib/utils/toast';
import usePost from 'lib/hooks/usePost';

function MergeManufacturersModal({ mergeList, setMergeList, onHide }) {
  const [filteredManufacturers, setFilteredManufacturers] = useState(mergeList);
  const [parentManufacturer, setParentManufacturer] = useState(null);

  useEffect(() => {
    //If there is only one verified manufacturer in mergeList it will be
    //set as the parent manufacturer.
    const verifiedManufacturer = mergeList.find(
      manufacturer => manufacturer.verification_status === ManufacturerVerificationStatus.VERIFIED,
    );
    const unverifiedManufacturers = mergeList.filter(
      manufacturer => manufacturer.verification_status !== ManufacturerVerificationStatus.VERIFIED,
    );

    if (
      verifiedManufacturer &&
      mergeList.filter(
        manufacturer =>
          manufacturer.verification_status === ManufacturerVerificationStatus.VERIFIED,
      ).length === 1
    ) {
      setFilteredManufacturers(unverifiedManufacturers);
      setParentManufacturer(verifiedManufacturer);
    }
  }, [mergeList]);

  const { postData: mergeManufacturers, loading } = usePost(
    GLOBAL_MANUFACTURERS_MERGE,
    () => {
      SwalDialog('success', 'Started merging manufacturers', 'Success', 'center');
      onHide();
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while merging manufacturers: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const options = mergeList.map((manufacturer, index) => ({
    label: manufacturer.name,
    value: index,
  }));

  const handleParentChange = option => {
    if (option) {
      setParentManufacturer(mergeList[option.value]);
      setFilteredManufacturers(
        mergeList.filter(mergeManufacturer => mergeManufacturer.id !== mergeList[option.value].id),
      );
    } else {
      setParentManufacturer(null);
      setFilteredManufacturers(mergeList);
    }
  };

  const handleRemoveManufacturer = manufacturerId => {
    setFilteredManufacturers(
      filteredManufacturers.filter(mergeManufacturer => mergeManufacturer.id !== manufacturerId),
    );
    setMergeList(mergeList.filter(mergeManufacturer => mergeManufacturer.id !== manufacturerId));
  };

  const handleMergeManufacturer = () => {
    const hasVerifiedManufacturer = filteredManufacturers.some(
      manufacturer => manufacturer.verification_status === ManufacturerVerificationStatus.VERIFIED,
    );

    if (parentManufacturer?.verification_status !== ManufacturerVerificationStatus.VERIFIED) {
      Swal.fire({
        icon: 'error',
        title: 'Parent Manufacturer Not Verified',
        text: 'Parent manufacturer is not verified.',
        confirmButtonText: 'OK',
      });
    } else if (hasVerifiedManufacturer) {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Merge',
        text: 'At least one verified manufacturer in the secondary manufacturers.',
        confirmButtonText: 'OK',
      });
    } else {
      mergeManufacturers({
        parentId: parentManufacturer.id,
        mergeIds: filteredManufacturers.map(manufacturer => manufacturer.id),
      });
    }
  };

  return (
    <Modal show={true} onHide={onHide} size="lg" centered>
      <div className={css(styles.modalContent)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>Merge Manufacturers</h2>
          <Button
            variant="link"
            className={css(styles.closeButton)}
            onClick={onHide}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
        <Modal.Body className={css(styles.body)}>
          <div className={css(styles.section)}>
            <h4 className={css(styles.sectionTitle)}>Parent Manufacturer</h4>
            <Select
              options={options}
              value={
                parentManufacturer
                  ? options.find(option => option.label === parentManufacturer.name)
                  : null
              }
              onChange={handleParentChange}
              placeholder="Select the primary manufacturer"
              styles={selectStyles}
            />
          </div>

          <div className={css(styles.section)}>
            <h4 className={css(styles.sectionTitle)}>Secondary Manufacturers</h4>
            <div className={css(styles.tableWrapper)}>
              <Table className={css(styles.table)}>
                <thead className={css(styles.tableHead)}>
                  <tr>
                    <th>Name</th>
                    <th>FSA ID</th>
                    <th>Cnd ID</th>
                    <th>Verified</th>
                    <th>Manufacturer GLN</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredManufacturers.map((manufacturer, index) => (
                    <tr key={index} className={css(styles.tableRow)}>
                      <td className={css(styles.manufacturerName)}>{manufacturer.name}</td>
                      <td>{manufacturer.id}</td>
                      <td>{manufacturer.cnd_id}</td>
                      <td>
                        <span
                          className={css(
                            styles.badge,
                            manufacturer.verification_status ===
                              ManufacturerVerificationStatus.VERIFIED
                              ? styles.badgeVerified
                              : styles.badgeUnverified,
                          )}
                        >
                          {manufacturer.verification_status ===
                          ManufacturerVerificationStatus.VERIFIED
                            ? 'Yes'
                            : 'No'}
                        </span>
                      </td>
                      <td>
                        {manufacturer.manufacturer_glns &&
                        Object.keys(manufacturer.manufacturer_glns).length > 0
                          ? Object.entries(manufacturer.manufacturer_glns).map(
                              ([gln, subCompanyName], glnIndex) => (
                                <div key={glnIndex}>
                                  {gln}
                                  {subCompanyName ? ` (${subCompanyName})` : ''}
                                </div>
                              ),
                            )
                          : '-'}
                      </td>
                      <td>
                        <Button
                          variant="link"
                          className={css(styles.removeButton)}
                          onClick={() => handleRemoveManufacturer(manufacturer.id)}
                          aria-label="Remove manufacturer"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Modal.Body>
        <div className={css(styles.footer)}>
          <Button
            className={css(styles.mergeButton)}
            disabled={filteredManufacturers?.length < 1 || loading || !parentManufacturer}
            onClick={handleMergeManufacturer}
          >
            {loading ? (
              <Spinner animation="border" role="status" size="sm" />
            ) : (
              'Merge Manufacturers'
            )}
          </Button>
          <Button className={css(styles.cancelButton)} onClick={() => onHide()}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${greyStroke}`,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: squidInk,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    padding: 8,
    borderRadius: 6,
    ':hover': {
      backgroundColor: `${grape}15`,
      color: grape,
    },
  },
  body: {
    padding: '24px',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 12,
  },
  tableWrapper: {
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  table: {
    marginBottom: 0,
  },
  tableHead: {
    backgroundColor: backgroundGrey5,
  },
  tableRow: {
    ':hover': {
      backgroundColor: `${grape}05`,
    },
  },
  manufacturerName: {
    fontWeight: 500,
    color: squidInk,
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeVerified: {
    backgroundColor: softGreenLight,
    color: softGreen,
  },
  badgeUnverified: {
    backgroundColor: softAmberLight,
    color: softAmber,
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: salmon,
    cursor: 'pointer',
    padding: 6,
    borderRadius: 4,
    ':hover': {
      backgroundColor: `${salmon}15`,
    },
  },
  footer: {
    display: 'flex',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${greyStroke}`,
    backgroundColor: backgroundGrey5,
  },
  mergeButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: grape,
    },
    ':disabled': {
      backgroundColor: greyStroke,
      color: coolGrey,
    },
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${grape}`,
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: `${grape}15`,
    },
  },
});

MergeManufacturersModal.propTypes = {
  mergeList: PropTypes.array,
  setMergeList: PropTypes.func,
  onHide: PropTypes.func,
};

export default MergeManufacturersModal;
